import React, {
  createContext,
  ReactElement,
  ReactNode,
  useContext,
  useEffect,
  useReducer
} from 'react'

import { getUtilityEnums } from '@pepper/utils/src/api/utils/utils'
import { fetchList } from '@pepper/utils/src/api/users/users'
import { filterVerticals } from '@pepper/utils/src/function/filterVerticals'
import { CREATOR_ROLES } from '@pepper/utils/src/constant/enum/user_roles'

import { useAuthState } from './AuthProvider'

const initialState = {
  feedback_questions: [],
  clientList: [],
  currencies: [],
  genres: [],
  industries: [],
  languages: [],
  timezones: [],
  verticals: [],
  sub_verticals: [],
  sub_genres: [],
  tone_of_voice: [],
  project_goals: [],
  education_degrees: [],
  country_dial_codes: [],
  feedback_checks: [],
  project_categories: [],
  translation_types: [],
  translation_word_usage_types: [],
  profession_categories: [],
  software: [],
  sub_categories: [],
  feedback_compliments: [],
  feedback_inputs: [],
  rework_areas: [],
  project_pay_type: [],
  modes_of_allotment: [],
  job_functions: [],
  sectors: [],
  work_models: [],
  work_type: [],
  user_tags: [],
  user_unavailability_reasons: []
}

export type ContextType = {
  clientList?: { id: number; title: string }[]
  feedback_questions: { id: number; title: string }[]
  currencies: {
    id: number
    multiplier: number | null
    name: string
    parent_id: number | null
    symbol: string
  }[]
  genres: IndustryGenre[]
  industries: { id: number; title: string }[]
  languages: { id: number; title: string; copy_test_file_url?: string }[]
  project_goals: { id: number; title: string }[]
  tone_of_voice: { id: number; title: string }[]
  timezones: Timezone[]
  verticals: VerticalObjectType[]
  sub_verticals: SubVertical[]
  sub_genres: SubGenres[]
  education_degrees: { id: number; name: string }[]
  country_dial_codes: {
    dial_code: string
    country_code: string
    country_name: string
  }[]
  feedback_checks: { id: number; name: string }[]
  project_categories: {
    id: number
    title: string
    key: ProjectCategory
    icon: string
    disabled?: boolean
  }[]
  translation_types: {
    id: number
    name: string
    key: string
  }[]
  translation_word_usage_types: {
    id: number
    name: string
    key: string
  }[]
  profession_categories: {
    category: { id: number; key: string; title: string }
    id: number
    name: string
  }[]
  software: {
    category: { id: number; key: string; title: string }
    id: number
    name: string
  }[]
  sub_categories: SubCategories[]
  feedback_compliments: {
    id: number
    title: string
    icon_set: string | { blue: string; default: string } | null
  }[]
  feedback_inputs: {
    id: number
    title: string
    icon_set: string | { blue: string; default: string } | null
  }[]
  rework_areas: { id: number; title: string; inputs: number[]; key: string }[]
  project_pay_type: { id: number; key: PaymentBase; name: string }[]
  modes_of_allotment: { id: number; key: string }[]
  job_functions: { id: number; key: string; title: string }[]
  sectors: { id: number; key: string; title: string }[]
  work_models: { id: number; key: string; title: string }[]
  work_type: { id: number; key: string; title: string }[]
  user_tags: { description: string; id: number; key: string }[]
  user_unavailability_reasons: {
    id: number
    key: string
    title: string
    created_at: string
    updated_at: string
  }[]
}

type ReducerType =
  | 'GET_UTILS_SUCCESS'
  | 'GET_UTILS_REQUEST'
  | 'GET_UTILS_FAILURE'
  | 'GET_CLIENT_LIST_SUCCESS'
  | 'GET_CLIENT_LIST_REQUEST'
  | 'GET_CLIENT_LIST_FAILURE'

const ConstantContext = createContext<ContextType>(initialState)

const reducer = (
  state: ContextType,
  action: { type: ReducerType; payload?: any }
) => {
  switch (action.type) {
    case 'GET_UTILS_SUCCESS': {
      const { user } = action.payload
      const userRole = user.role
      const company = user?.company_details?.find((_company: Company) =>
        window.location.pathname.includes(_company.id)
      )
      const clientType =
        company?.type === 'enterprise' ? 'client_enterprise' : 'client_sme'

      const nextState = {
        ...state,
        ...action.payload.data,
        verticals: filterVerticals(
          action.payload.data.verticals,
          userRole,
          userRole in CREATOR_ROLES ? 'creator' : clientType
        )
      }

      return nextState
    }
    case 'GET_UTILS_REQUEST':
    case 'GET_UTILS_FAILURE':
      return state
    case 'GET_CLIENT_LIST_SUCCESS': {
      const clientList = action.payload.map((item: any) => ({
        id: item.id,
        title: item.company_name || item.full_name
      }))
      return { ...state, clientList }
    }
    case 'GET_CLIENT_LIST_REQUEST':
    case 'GET_CLIENT_LIST_FAILURE':
      return state
    default:
      return state
  }
}

/* reducer actions */
const handleUtilityApi = async (
  dispatch: React.Dispatch<{
    type: ReducerType
    payload?: any
  }>,
  user: User
): Promise<void> => {
  const MAX_RETRIES = 10
  let retries = 0
  while (retries <= MAX_RETRIES) {
    try {
      const response = await getUtilityEnums()
      if (response.status !== 'ok') {
        throw new Error('Could not fetch utils')
      }
      dispatch({
        type: 'GET_UTILS_SUCCESS',
        payload: { data: response.data, user }
      })
      break
    } catch (error) {
      if (retries < MAX_RETRIES) {
        dispatch({ type: 'GET_UTILS_REQUEST' })
        retries += 1
      } else {
        dispatch({ type: 'GET_UTILS_FAILURE' })
        retries += 1
      }
    }
  }
}

const handleClientList = async (
  dispatch: React.Dispatch<{
    type: ReducerType
    payload?: any
  }>
): Promise<void> => {
  const MAX_RETRIES = 10
  let retries = 0
  while (retries <= MAX_RETRIES) {
    try {
      const response = await fetchList({
        query_params: { role: 'client' },
        fetchAll: true
      })

      if (response.status !== 'ok') {
        throw new Error('Could not fetch client list')
      }
      dispatch({
        type: 'GET_CLIENT_LIST_SUCCESS',
        payload: response.data.records
      })
      return
    } catch (error) {
      if (retries < MAX_RETRIES) {
        dispatch({ type: 'GET_CLIENT_LIST_REQUEST' })
        retries += 1
      } else {
        dispatch({ type: 'GET_CLIENT_LIST_FAILURE' })
        retries += 1
      }
    }
  }
}

const UtilsProvider = ({ children }: { children: ReactNode }): ReactElement => {
  const [state, dispatch] = useReducer(reducer, initialState)
  const { user } = useAuthState()

  useEffect(() => {
    handleUtilityApi(dispatch, user)
    const internalRole: UserRole[] = ['client_ops', 'editor_ops', 'writer_ops']

    if (user?.role && internalRole.includes(user.role)) {
      handleClientList(dispatch)
    }
  }, [user])

  return (
    <ConstantContext.Provider value={state}>
      {children}
    </ConstantContext.Provider>
  )
}

// hooks for accessing context
const useUtils = (): ContextType => {
  const context = useContext(ConstantContext)
  if (context === undefined) {
    throw new Error('useUtils must be used within a UtilsProvider')
  }
  return context
}

export { UtilsProvider, useUtils }
